/* eslint-disable jsx-a11y/label-has-associated-control */
import { Chip, FormControl, MenuItem, Select, TextField } from '@material-ui/core';
import { XIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import { FC } from 'react';
import CategoryTestsAutocomplete from '../../shared/components/CategoryTestsAutocomplete';
import SearchAutocomplete from '../../shared/components/SearchAutocomplete';
import { CategoryTestOption, ServiceOption } from '../../shared/types';
import { needsAttachmentManager } from '../../shared/utils';
import { useStyles } from '../styles';
import { InfosForm, ManagerOption, RoleOption } from '../types';

interface StepInfosProps {
  infos: InfosForm;
  roles: RoleOption[];
  managers: ManagerOption[];
  services: ServiceOption[];
  categoryTests: CategoryTestOption[];
  showServices?: boolean;
  onChange: (patch: Partial<InfosForm>) => void;
}

const managerLabel = (manager: ManagerOption) =>
  `${manager.firstname || ''} ${manager.lastname || ''}`.trim();

const StepInfos: FC<StepInfosProps> = ({
  infos,
  roles,
  managers,
  services,
  categoryTests,
  showServices = true,
  onChange,
}) => {
  const classes = useStyles();
  const fieldClass = clsx(classes.textField, classes.fullField);
  const showManager = needsAttachmentManager(infos.roleId);
  const selectedManager =
    managers.find(manager => Number(manager.id) === Number(infos.managerId)) || null;
  const selectedServices = services.filter(service =>
    infos.serviceIds.includes(Number(service.id))
  );
  const selectedCategories = categoryTests.filter(category =>
    infos.categoryTestIds.includes(category.id)
  );

  return (
    <div>
      <h2 className={classes.sectionTitle}>Informations</h2>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-role">
          Rôle
        </label>
        <FormControl variant="outlined" className={fieldClass}>
          <Select
            id="consultant-role"
            value={infos.roleId}
            displayEmpty={true}
            onChange={event => {
              const { value } = event.target;
              onChange({ roleId: value === '' ? '' : Number(value), managerId: '' });
            }}>
            <MenuItem value="">
              <span className={classes.placeholder}>Sélectionner un rôle</span>
            </MenuItem>
            {roles.map(role => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-firstname">
          Prénom
        </label>
        <TextField
          id="consultant-firstname"
          className={fieldClass}
          variant="outlined"
          value={infos.firstname}
          onChange={event => onChange({ firstname: event.target.value })}
        />
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-lastname">
          Nom
        </label>
        <TextField
          id="consultant-lastname"
          className={fieldClass}
          variant="outlined"
          value={infos.lastname}
          onChange={event => onChange({ lastname: event.target.value })}
        />
      </div>

      {showManager && (
        <div className={classes.field}>
          <label className={classes.label} htmlFor="consultant-manager">
            Responsable
          </label>
          <SearchAutocomplete
            id="consultant-manager"
            options={managers}
            value={selectedManager}
            placeholder="Rechercher un nom ou un prénom"
            getOptionLabel={managerLabel}
            onChange={selected => {
              const manager = selected as ManagerOption | null;
              onChange({ managerId: manager ? Number(manager.id) : '' });
            }}
          />
        </div>
      )}

      {showServices && (
        <div className={classes.field}>
          <label className={classes.label} htmlFor="consultant-services">
            Prestation(s)
          </label>
          <SearchAutocomplete
            id="consultant-services"
            multiple={true}
            options={services}
            value={selectedServices}
            placeholder="Rechercher une prestation"
            getOptionLabel={option => option.name}
            onChange={selected => {
              const next = (selected as ServiceOption[]) || [];
              onChange({ serviceIds: next.map(service => Number(service.id)) });
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  className={classes.serviceChip}
                  label={option.name}
                  style={{ margin: 2 }}
                  deleteIcon={<XIcon size={14} />}
                />
              ))
            }
          />
        </div>
      )}

      {showServices && (
        <div className={classes.field}>
          <label className={classes.label} htmlFor="consultant-categories">
            Catégories de tests mineurs
          </label>
          <CategoryTestsAutocomplete
            id="consultant-categories"
            options={categoryTests}
            value={selectedCategories}
            onChange={next => onChange({ categoryTestIds: next.map(category => category.id) })}
          />
        </div>
      )}
    </div>
  );
};

export default StepInfos;
